import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from "react-router-dom";
import { Dropdown } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import Icon from '@mdi/react';
import { mdiAccountCircle, mdiClose, mdiLogout, mdiMenu, mdiMenuDown, mdiShieldAccount } from '@mdi/js';
import { toggleDarkMode } from '../reducers/theme';
import { fetchMe, selectCanSeeAdmin } from '../reducers/user';
import { resetAdminApiResponse } from '../reducers/admin';
import { adminMessage } from '../partials/adminFormat';
import { fetchEnrollment, resetApiResponse } from '../reducers/enrollments';

function Navbar() {

  const darkMode = useSelector((state) => state.theme.darkMode);
  const firstname = useSelector((state) => state.user.firstname);
  const lastname = useSelector((state) => state.user.lastname);
  const enrollment = useSelector((state) => state.enrollments.enrollment);
  const enrollmentApiResponse = useSelector((state) => state.enrollments.apiResponse);
  const adminApiResponse = useSelector((state) => state.admin.apiResponse);
  const canSeeAdmin = useSelector(selectCanSeeAdmin);

  const [toastMessage, setToastMessage] = useState('');
  // Bumped on every response, so the same message twice in a row still shows again.
  const [toastId, setToastId] = useState(0);

  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();

  // Below 992px the sidebar is off canvas: it slides in while it has .active (_sidebar.scss), and goes away on a new page.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  useEffect(() => setSidebarOpen(false), [pathname]);
  useEffect(() => {
    document.getElementById('sidebar')?.classList.toggle('active', sidebarOpen);
    // The page behind the open drawer does not scroll (_sidebar.scss).
    document.body.classList.toggle('sidebar-drawer-open', sidebarOpen);
  }, [sidebarOpen, pathname]);

  useEffect(() => {
    if (darkMode)
      document.documentElement.setAttribute("data-theme", "dark");
    else
      document.documentElement.setAttribute("data-theme", "light");
  }, [darkMode]);

  useEffect(() => {
    if (enrollmentApiResponse.status !== null) {
      if (enrollmentApiResponse.thunk === 'updateFuturePedagoStatus') {
        if (enrollmentApiResponse.status === 'success') {
          setToastMessage('Le devenir a bien été mis à jour');
        } else {
          setToastMessage('Une erreur est survenue lors de la mise à jour du devenir');
        }
      } else if (enrollmentApiResponse.thunk === 'updateFutureDestination') {
        if (enrollmentApiResponse.status === 'success') {
          setToastMessage('La destination Tech 4 a bien été mise à jour');
        } else {
          setToastMessage('Une erreur est survenue lors de la mise à jour de la destination Tech 4');
        }
      } else if (enrollmentApiResponse.thunk === 'reenroll') {
        if (enrollmentApiResponse.status === 'success') {
          setToastMessage('L\'opération de réinscription a bien été effectuée');
        } else {
          setToastMessage('Une erreur est survenue lors de l\'opération de réinscription');
        }
      }
      if (enrollmentApiResponse.status === 'success') {
        if ((enrollment?._id !== undefined) && (enrollment?._id !== null))
          dispatch(fetchEnrollment({ id: enrollment._id }));
      }
      dispatch(resetApiResponse());
      // Only the three thunks above have a message; another one would flash the previous text.
      if (['updateFuturePedagoStatus', 'updateFutureDestination', 'reenroll'].includes(enrollmentApiResponse.thunk))
        setToastId((id) => id + 1);
    }
  }, [enrollmentApiResponse, enrollment?._id, dispatch]);

  useEffect(() => {
    if (adminApiResponse.status === null) return;
    setToastMessage(adminMessage(adminApiResponse));
    setToastId((id) => id + 1);
    dispatch(resetAdminApiResponse());
  }, [adminApiResponse, dispatch]);

  // A new toast clears the timers of the previous one, so it gets its full 3 seconds.
  useEffect(() => {
    if (toastId === 0) return;
    const toast = document.getElementById('navbar-toast');
    toast.style.opacity = 1;
    const hide = setTimeout(() => { toast.style.opacity = 0; }, 3000);
    const clear = setTimeout(() => setToastMessage(''), 4000);
    return () => {
      clearTimeout(hide);
      clearTimeout(clear);
    };
  }, [toastId]);
  
  useEffect(() => {
    if ((firstname === null) || (lastname === null)) dispatch(fetchMe());
  }, [firstname, lastname, dispatch]);

  function toggleMode() {
    dispatch(toggleDarkMode());
  }

  return (
    <>
      <nav className="navbar p-0 fixed-top d-flex flex-row flex-nowrap">
        {/* The logo sits in the navbar, which spans the whole width above the sidebar. */}
        <div className="navbar-brand-wrapper d-flex align-items-center justify-content-center">
          <a className="navbar-brand brand-logo d-none d-lg-block" href="/">
            <img src={require('../../assets/images/epitech_logo.png')} alt="Logo Epitech" />
          </a>
          <a className="navbar-brand brand-logo-mini d-lg-none" href="/">
            <img src={require('../../assets/images/epitech_logo_mini.png')} alt="Logo Epitech" />
          </a>
        </div>
        <div className="navbar-menu-wrapper flex-grow d-flex align-items-stretch">
          <ul className="navbar-nav navbar-nav-left">
            <li className="nav-item" id="navbar-toast">
              {toastMessage}
            </li>
          </ul>
          <ul className="navbar-nav navbar-nav-right">
            <Dropdown alignRight as="li" className="nav-item">
              <Dropdown.Toggle as="a" className="nav-link cursor-pointer" bsPrefix="no-caret" aria-label="Profil">
                <div className="navbar-profile">
                  <Icon path={mdiAccountCircle} className="d-sm-none" size={1} />
                  <p className="mb-0 d-none d-sm-block navbar-profile-name">{firstname} {lastname}</p>
                  <Icon path={mdiMenuDown} className="d-none d-sm-block" size="16" />
                </div>
              </Dropdown.Toggle>
              <Dropdown.Menu className="navbar-dropdown dropdown-menu-right preview-list navbar-profile-dropdown-menu">
                <h6 className="p-3 mb-0">Profil</h6>
                <Dropdown.Divider />
                <Dropdown.Item as="button" type="button" onClick={() => toggleMode()} className="preview-item">
                  <div className="dropdown-item preview-item">
                    {/* Not a <label>: wrapping the checkbox, it turned one click into two and the theme switched back. */}
                    <span className="themeToggle">
                      <span>Light</span>
                      <span className="themeToggle__toggle-wrap">
                        <input
                          type="checkbox"
                          className="themeToggle__toggle"
                          id="themeToggle"
                          role="switch"
                          name="themeToggle"
                          value="dark"
                          checked={darkMode}
                          readOnly
                        />
                        <span className="themeToggle__fill"></span>
                        <span className="themeToggle__icon">
                          <span className="themeToggle__icon-part"></span>
                          <span className="themeToggle__icon-part"></span>
                          <span className="themeToggle__icon-part"></span>
                          <span className="themeToggle__icon-part"></span>
                          <span className="themeToggle__icon-part"></span>
                          <span className="themeToggle__icon-part"></span>
                          <span className="themeToggle__icon-part"></span>
                          <span className="themeToggle__icon-part"></span>
                          <span className="themeToggle__icon-part"></span>
                        </span>
                      </span>
                      <span>Dark</span>
                    </span>
                  </div>
                </Dropdown.Item>
                <Dropdown.Divider />
                {canSeeAdmin && (
                  <>
                    <Dropdown.Item href="!#" onClick={evt => { evt.preventDefault(); history.push('/admin'); }} className="preview-item">
                      <div className="preview-thumbnail">
                        <div className="preview-icon bg-dark rounded-circle">
                          <Icon path={mdiShieldAccount} size='20' className='text-primary' />
                        </div>
                      </div>
                      <div className="preview-item-content">
                        <p className="preview-subject mb-1">Administration</p>
                      </div>
                    </Dropdown.Item>
                    <Dropdown.Divider />
                  </>
                )}
                <Dropdown.Item href="!#" onClick={evt => { evt.preventDefault(); history.push('/logout'); }} className="preview-item">
                  <div className="preview-thumbnail">
                    <div className="preview-icon bg-dark rounded-circle">
                      <Icon path={mdiLogout} size='20' className='text-danger' />
                    </div>
                  </div>
                  <div className="preview-item-content">
                    <p className="preview-subject mb-1">Déconnexion</p>
                  </div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </ul>
          <button className="navbar-toggler navbar-toggler-right d-lg-none align-self-center" type="button" aria-label="Menu latéral" aria-controls="sidebar" aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Icon path={sidebarOpen ? mdiClose : mdiMenu} size={1} />
          </button>
        </div>
      </nav>
      {sidebarOpen && <div className="sidebar-backdrop d-lg-none" onClick={() => setSidebarOpen(false)} />}
    </>
  );
}

export default Navbar;
